import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AGREEMENT_TYPE_LABELS,
  BILLING_TIMINGS,
  BILLING_TIMING_LABELS,
  PSA_CLASSES,
  PSA_CLASS_LABELS,
  type Agreement,
  type BillingTiming,
  type CreateProductInput,
  type Product,
  type PsaClass,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { DataState } from '../../components/ui/DataState.js';
import { CheckboxField, SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { formatDate } from '../../utils/date.js';
import { useAgreements } from '../agreements/api.js';
import { AGREEMENT_STATUS_LABELS, AGREEMENT_STATUS_TONES } from '../agreements/status.js';
import { useServiceCategoryOptions } from '../service-categories/api.js';
import { useCreateProduct, useDeleteProduct, useUpdateProduct } from './api.js';

interface ProductFormModalProps {
  isOpen: boolean;
  /** Existing product to edit, or null to create a new one. */
  product: Product | null;
  onClose: () => void;
}

interface FormState {
  sku: string;
  name: string;
  description: string;
  customerDescription: string;
  serviceCategoryId: string;
  qboClass: string;
  psaClass: '' | PsaClass;
  unitPrice: string;
  billingTiming: BillingTiming;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  sku: '',
  name: '',
  description: '',
  customerDescription: '',
  serviceCategoryId: '',
  qboClass: '',
  psaClass: '',
  unitPrice: '',
  billingTiming: 'advance',
  isActive: true,
};

function toFormState(product: Product | null): FormState {
  if (!product) {
    return EMPTY_FORM;
  }
  return {
    sku: product.sku,
    name: product.name,
    description: product.description ?? '',
    customerDescription: product.customerDescription ?? '',
    serviceCategoryId: product.serviceCategoryId ?? '',
    qboClass: product.qboClass ?? '',
    psaClass: product.psaClass ?? '',
    unitPrice: product.unitPrice,
    billingTiming: product.billingTiming,
    isActive: product.isActive,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

/** Lists every agreement that has this product on one of its lines. */
function ProductAgreementsTab({ productId }: { productId: string }): JSX.Element {
  const navigate = useNavigate();
  const query = useAgreements({ page: 1, pageSize: 200, productId });
  const rows = query.data?.data ?? [];
  return (
    <table className="w-full text-left text-sm">
      <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-4 py-3 font-medium">Name</th>
          <th className="px-4 py-3 font-medium">Customer</th>
          <th className="px-4 py-3 font-medium">Type</th>
          <th className="px-4 py-3 font-medium">Status</th>
          <th className="px-4 py-3 font-medium">Start</th>
          <th className="px-4 py-3 font-medium">End</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        <DataState
          colSpan={6}
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
          isEmpty={rows.length === 0}
          emptyTitle="No agreements use this product"
          emptyDescription="Agreements that include this product as a line will appear here."
        >
          {rows.map((a: Agreement) => (
            <tr
              key={a.id}
              className="cursor-pointer hover:bg-slate-50"
              onClick={() => navigate(`/agreements/${a.id}`)}
            >
              <td className="px-4 py-3 font-medium text-slate-900">{a.name}</td>
              <td className="px-4 py-3 text-slate-600">{a.customerName || '—'}</td>
              <td className="px-4 py-3 text-slate-600">{AGREEMENT_TYPE_LABELS[a.type]}</td>
              <td className="px-4 py-3">
                <Badge tone={AGREEMENT_STATUS_TONES[a.status]}>
                  {AGREEMENT_STATUS_LABELS[a.status]}
                </Badge>
              </td>
              <td className="px-4 py-3 text-slate-600">{formatDate(a.startDate)}</td>
              <td className="px-4 py-3 text-slate-600">{formatDate(a.endDate)}</td>
            </tr>
          ))}
        </DataState>
      </tbody>
    </table>
  );
}

export function ProductFormModal({
  isOpen,
  product: productProp,
  onClose,
}: ProductFormModalProps): JSX.Element {
  const [product, setProduct] = useState<Product | null>(productProp);
  const isEdit = product !== null;
  const { isAdmin } = useAuth();
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(() => toFormState(product));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tab, setTab] = useState<'details' | 'agreements'>('details');
  const serviceCategoriesQuery = useServiceCategoryOptions();

  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const deleteMutation = useDeleteProduct();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  useEffect(() => {
    if (isOpen) {
      setProduct(productProp);
    }
  }, [isOpen, productProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(product));
      setConfirmOpen(false);
      setTab('details');
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, product, resetSaveMode, resetCreate, resetUpdate]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save product.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const payload: CreateProductInput = {
      sku: form.sku.trim(),
      name: form.name.trim(),
      description: form.description.trim() ? form.description.trim() : null,
      customerDescription: form.customerDescription.trim()
        ? form.customerDescription.trim()
        : null,
      serviceCategoryId: form.serviceCategoryId ? form.serviceCategoryId : null,
      qboClass: form.qboClass.trim() ? form.qboClass.trim() : null,
      psaClass: form.psaClass ? form.psaClass : null,
      unitPrice: form.unitPrice.trim(),
      billingTiming: form.billingTiming,
      isActive: form.isActive,
    };

    if (isEdit) {
      updateMutation.mutate(
        { id: product.id, input: payload },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setProduct(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!product) {
      return;
    }
    deleteMutation.mutate(product.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit product' : 'New product'}
      size={isEdit ? 'lg' : 'md'}
      tabs={
        isEdit
          ? [
              { id: 'details', label: 'Details' },
              { id: 'agreements', label: 'Agreements' },
            ]
          : undefined
      }
      activeTabId={tab}
      onTabChange={(id) => setTab(id as 'details' | 'agreements')}
      footer={
        tab === 'details' ? (
          <FormActions
            formId="product-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit && isAdmin ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            {isEdit && isAdmin ? (
              <Button
                variant="ghost"
                className="mr-auto text-rose-600 hover:bg-rose-50"
                onClick={() => setConfirmOpen(true)}
                disabled={mutation.isPending || deleteMutation.isPending}
              >
                Delete
              </Button>
            ) : null}
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </>
        )
      }
    >
      {isEdit && tab === 'agreements' && product ? (
        <ProductAgreementsTab productId={product.id} />
      ) : null}
      <form
        id="product-form"
        onSubmit={handleSubmit}
        className={`flex flex-col gap-4 ${isEdit && tab !== 'details' ? 'hidden' : ''}`}
      >
        {generalError ? (
          <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{generalError}</div>
        ) : null}

        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="SKU"
            required
            value={form.sku}
            placeholder="PROD-001"
            maxLength={50}
            onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
            error={fieldErrors.sku}
          />
          <TextField
            label="Unit price"
            required
            inputMode="decimal"
            value={form.unitPrice}
            placeholder="150.00"
            onChange={(e) => setForm((f) => ({ ...f, unitPrice: e.target.value }))}
            error={fieldErrors.unitPrice}
          />
        </div>

        <TextField
          label="Name"
          required
          value={form.name}
          maxLength={200}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          error={fieldErrors.name}
        />

        <TextAreaField
          label="Description"
          rows={3}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          error={fieldErrors.description}
        />

        <TextAreaField
          label="Customer description"
          rows={3}
          value={form.customerDescription}
          onChange={(e) => setForm((f) => ({ ...f, customerDescription: e.target.value }))}
          error={fieldErrors.customerDescription}
        />

        <div className="grid grid-cols-2 gap-4">
          <SelectField
            label="Service category"
            value={form.serviceCategoryId}
            options={[
              { value: '', label: 'None' },
              ...(serviceCategoriesQuery.data ?? []).map((category) => ({
                value: category.id,
                label: category.name,
              })),
            ]}
            onChange={(e) => setForm((f) => ({ ...f, serviceCategoryId: e.target.value }))}
            error={fieldErrors.serviceCategoryId}
          />
          <TextField
            label="QBO class"
            value={form.qboClass}
            maxLength={256}
            onChange={(e) => setForm((f) => ({ ...f, qboClass: e.target.value }))}
            error={fieldErrors.qboClass}
          />
        </div>

        <SelectField
          label="PSA class"
          value={form.psaClass}
          options={[
            { value: '', label: 'None' },
            ...PSA_CLASSES.map((cls) => ({ value: cls, label: PSA_CLASS_LABELS[cls] })),
          ]}
          onChange={(e) => setForm((f) => ({ ...f, psaClass: e.target.value as '' | PsaClass }))}
          error={fieldErrors.psaClass}
        />

        <SelectField
          label="Billing timing"
          value={form.billingTiming}
          options={BILLING_TIMINGS.map((timing) => ({
            value: timing,
            label: BILLING_TIMING_LABELS[timing],
          }))}
          onChange={(e) =>
            setForm((f) => ({ ...f, billingTiming: e.target.value as BillingTiming }))
          }
          error={fieldErrors.billingTiming}
        />

        <CheckboxField
          label="Active"
          checked={form.isActive}
          onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
        />
      </form>
    </Modal>
    <ConfirmDialog
      isOpen={confirmOpen}
      title="Delete product"
      message={`Delete "${product?.name ?? ''}"? This action cannot be undone.`}
      confirmLabel="Delete"
      destructive
      isConfirming={deleteMutation.isPending}
      onConfirm={handleDelete}
      onCancel={() => setConfirmOpen(false)}
    />
    </>
  );
}
